<style scoped>
.modal-content {
    width: 90vw;
}
</style>
<template>
    <div class="modal-content" ref="container">
        <form>
            <div class="modal-header clearfix">
                <h3 class="modal-title float-left">Upload spreadsheet for {{ $props.name }} <strong v-if="$props.testMode" class="badge badge-warning ml-1">Test Data</strong></h3>
                <button type="button" aria-label="Close" class="close icon icon-close" @click="close()" />
            </div>
            <div class="modal-body">
                <div class="clearfix">
                    <div describedby-text class="alert alert-warning">
                        <p class="mb-0">Uploading a spreadsheet will overwrite the existing data source.</p>
                    </div>
                    <div class="form-group" v-if="!$props.testMode && $props.canTestEdit && !$props.noTestModeSegregation">
                        <label class="control-label">
                            Overwrite both test and actual data
                            <span class="switch">
                                <input type="checkbox" name="bothTestAndNonTest" v-model="bothTestAndNonTest" />
                                <span class="slider"></span>
                            </span>
                        </label>
                    </div>
                    <div class="form-group">
                        <label class="sr-only" for="uploadSpreadsheet">Upload file</label>
                        <input type="hidden" name="dummy" value="dummy" />
                        <input type="file" name="file" id="uploadSpreadsheet" style="width: auto; display: inline-block" ref="fileElement" @change="previewUpload()" />
                    </div>
                    <div aria-live="polite">
                        <span class="sr-only" v-show="preview.status == 'ok'">
                            <span class="sr-only">File uploaded successfully.</span>
                        </span>
                        <LoadingWheel class="" v-if="preview.status == 'in-progress'" style="position: relative; top: -2px" :width="16" aria-hidden="true"></LoadingWheel>
                        <div v-show="preview.status == 'fail'" class="alert alert-danger m-0 mb-2 p-2">
                            <span class="icon icon-attention pr-1 sr-only" role="img" aria-label="Error:"></span>
                            <template v-for="error in preview.errors">
                                <p>{{ error }}</p>
                                <template v-if="error == 'Uploaded file must match the existing schema.'">
                                    <p v-if="expectedKeys.length > 0" class="mb-0">
                                        <strong>New Columns: </strong>
                                        <span>{{ expectedKeys.join(", ") }}</span>
                                    </p>
                                    <p v-if="missingKeys.length > 0">
                                        <strong>Missing Columns: </strong>
                                        <span>{{ missingKeys.join(", ") }}</span>
                                    </p>
                                </template>
                            </template>
                        </div>
                        <div class="form-group my-2 float-left" v-if="((preview.status == 'ok' || preview.status == 'fail') && !arrayEquals(preview.expectedKeys ?? [], preview.keys ?? [])) || overrideSchema">
                            <label class="control-label">
                                Override existing schema
                                <span class="switch">
                                    <input type="checkbox" id="overrideSchemaSwitch" name="overrideSchema" v-model="overrideSchema" />
                                    <span class="slider"></span>
                                </span>
                            </label>
                        </div>
                        <div class="alert alert-warning p-2 m-0 ml-2 float-left" v-show="overrideSchema">Changes to the schema can break any engagement that uses this data source.</div>
                    </div>
                    <span v-show="preview.data != null" class="data-source-preview float-right">
                        <span style="color: green" class="icon icon-check align-text-bottom" aria-hidden="true"></span>
                        Showing first {{ preview.data == null ? 0 : preview.data.data.length }} rows. Full file has {{ preview.count }} rows.
                    </span>
                </div>
                <div class="table-responsive" v-if="preview.data" style="max-height: 60vh">
                    <table class="table table-bordered" style="background: white; margin-bottom: 0">
                        <thead>
                            <tr style="background: #f8f8f8">
                                <th v-for="key in preview.data.keys">
                                    {{ key }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in preview.data.data">
                                <td v-for="column in row">
                                    {{ column }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="modal-footer flex-column align-items-end" style="gap: 0.5rem">
                <div v-if="(confirm.errors || []).length > 0">
                    <span class="help-block" v-for="error in confirm.errors">{{ error }}</span>
                </div>
                <div>
                    <button type="button" class="btn btn-primary" :disabled="confirm.status == 'in-progress'" @click="confirmUpload" id="importBtn">
                        Import
                        <span v-show="confirm.status == 'fail'" style="color: red" class="icon icon-attention"></span>
                        <LoadingWheel v-if="confirm.status == 'in-progress'" style="position: relative; top: -2px; display: inline-block" :width="16"></LoadingWheel>
                    </button>
                    <button type="button" class="btn btn-bare" @click="close">Cancel</button>
                </div>
            </div>
        </form>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext, watch } from "vue";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import { getXsftHeader } from "@/lib/getXsrfToken.3";
import { useMainStore } from "@/mainStore";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { ConvertFileResult } from "@cs-ts/convert-file-result";
import arrayEquals from "@/lib/arrayEquals";
type Props = {
    name: string;
    testMode: boolean;
    canTestEdit: boolean;
    noTestModeSegregation: boolean;
    confirmCallback: (data: Record<string, unknown>) => void;
} & DialogProps;
interface Datasource {
    status: string | null;
    expectedKeys: string[] | null;
    keys: string[] | null;
    errors: string[];
    data: {
        keys: (string | null)[];
        data: (string | null)[][];
    } | null;
    count: number | null;
}
export default defineComponent({
    props: ["name", "testMode", "canTestEdit", "noTestModeSegregation", "confirmCallback", "triggerElement"],
    components: {
        LoadingWheel,
    },
    setup(props: Props, ctx: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const mainStore = useMainStore();
        const fileElement = ref(null as unknown as HTMLInputElement);
        const bothTestAndNonTest = ref(false as boolean);
        const overrideSchema = ref(false as boolean);
        const initialInfo = {
            status: null,
            errors: [],
            data: null,
            count: null,
            expectedKeys: [],
            keys: [],
        };
        const preview = ref(structuredClone(initialInfo) as unknown as Datasource);
        const confirm = ref(structuredClone(initialInfo) as unknown as Datasource);

        const expectedKeys = computed(() => preview.value.keys!.filter(item => !preview.value.expectedKeys!.includes(item)));
        const missingKeys = computed(() => preview.value.expectedKeys!.filter(item => !preview.value.keys!.includes(item)));

        const previewUpload = () => {
            uploadFile("preview");
        };
        const close = () => {
            ctx.emit("close");
        };
        const confirmUpload = async () => {
            await uploadFile("save");
            await props.confirmCallback({
                keys: confirm.value!.data!.keys,
                data: confirm.value!.data!.data,
                count: confirm.value!.count,
            });
            close();
        };

        watch(
            () => overrideSchema.value,
            () => previewUpload(),
        );
        const uploadFile = async (subaction: string) => {
            const isPreview = subaction == "preview";
            const typeData = isPreview ? preview.value : confirm.value;
            const files = fileElement.value.files;
            if (files!.length == 0) {
                if (isPreview) {
                    typeData.errors = [];
                    typeData.status = null;
                    typeData.data = null;
                    typeData.count = null;
                } else {
                    debugger; // TODO
                }
            } else {
                typeData.status = "in-progress";
                typeData.errors = [];

                const uploadData = new FormData();
                const newFile = files![0];
                uploadData.append("subAction", subaction);
                uploadData.append("name", props.name);
                uploadData.append("testMode", props.testMode.toString());
                uploadData.append("bothTestAndNonTest", bothTestAndNonTest.value.toString());
                uploadData.append("overrideSchema", overrideSchema.value.toString());
                uploadData.append("file", newFile, newFile.name);

                const uploadUrl = await mainStore.getUrl("adminDataSourceConvertFile");
                try {
                    const response = await fetch(uploadUrl, {
                        method: "POST",
                        headers: await getXsftHeader(mainStore),
                        credentials: "same-origin",
                        body: uploadData,
                    });
                    throwIfNonSuccessStatus(response);
                    const responseJson = (await response.json()) as ConvertFileResult;
                    if (responseJson.success) {
                        typeData.status = "ok";
                        typeData.count = responseJson.rowCount;
                        typeData.expectedKeys = responseJson.expectedKeys;
                        typeData.keys = responseJson.keys;
                        typeData.data = {
                            keys: responseJson.keys,
                            data: responseJson.data!,
                        };
                    } else {
                        typeData.status = "fail";
                        typeData.errors = responseJson.errors;
                        typeData.expectedKeys = responseJson.expectedKeys;
                        typeData.keys = responseJson.keys;
                        typeData.count = null;
                        typeData.data = null;
                    }
                } catch (e) {
                    typeData.status = "fail";
                    typeData.errors = ["Unknown error occurred"];
                    throw e;
                }
            }
        };

        return {
            // Element refs
            fileElement,
            container: containerRef,

            // States
            preview,
            confirm,
            bothTestAndNonTest,
            overrideSchema,

            // Computed
            expectedKeys,
            missingKeys,

            // Methods
            previewUpload,
            close,
            confirmUpload,
            arrayEquals,
        };
    },
});
</script>
